import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuctionPeriods, getConnectionsSummary, getConnectionsDaily, getTopAccepted, getRewarded, getAcceptedPartners } from '../api';
import {
  Paper, Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Button, Chip, Alert
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import StatCard from './common/StatCard';
import ConversationModal from './ConversationModal';
import { useLiveActivity } from '../hooks/useLiveActivity';
import { bookedCallsNote, mergeLiveConnections } from '../utils/liveActivity';

// How each open status reads to someone who does not know the column values.
const STATUS_CHIP = {
  accepted: { label: 'Active', color: 'success' },
  pending: { label: 'Awaiting creator', color: 'warning' },
};
// An accepted video request is a booked call, not a live conversation.
const BOOKED_CHIP = { label: 'Booked', color: 'info' };
const CONNECTION_TYPE = { instant_connect: 'Instant Connect', bid_to_connect: 'Winning bid' };
const when = (d) => (d ? dayjs(d).format('MMM D, h:mm A') : '—');

export default function ConnectionsReport() {
  const theme = useTheme();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPair, setChatPair] = useState(null); // { userId, partnerId, userName, partnerName }
  const { data: live, error: liveError } = useLiveActivity();
  const liveConnections = live?.connections;
  const liveRows = useMemo(() => mergeLiveConnections(liveConnections), [liveConnections]);

  // ownerRow = the creator (the side that was bid on)
  // partnerRow = the fan who connected with them
  const handleViewConversation = (ownerRow, partnerRow) => {
    setChatPair({
      userId: ownerRow.userId,
      partnerId: partnerRow.partnerId,
      userName: ownerRow.username,
      partnerName: partnerRow.username,
      userRole: 'recipient',
      partnerRole: 'bidder'
    });
    setChatOpen(true);
  };
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('all');
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [topAccepted, setTopAccepted] = useState([]);
  const [rewarded, setRewarded] = useState([]);
  const [loading, setLoading] = useState(true);
  // Expandable "Accepted" row state
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [acceptedPartners, setAcceptedPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  useEffect(() => { getAuctionPeriods().then(setPeriods).catch(()=>{}); }, []);
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, d, ta, rw] = await Promise.all([
        getConnectionsSummary({ periodId }),
        getConnectionsDaily({ periodId }),
        getTopAccepted({ periodId, limit: 50 }),
        getRewarded({ periodId, limit: 50 })
      ]);
      setSummary(s); setDaily(d); setTopAccepted(ta); setRewarded(rw);
    } finally { setLoading(false); }
  };
  useEffect(() => { 
    setExpandedUserId(null);
    setAcceptedPartners([]);
    fetchAll(); /* eslint-disable-next-line */ 
  }, [periodId]);

  const handleAcceptedClick = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setAcceptedPartners([]);
      return;
    }
    setExpandedUserId(userId);
    setPartnersLoading(true);
    try {
      const rows = await getAcceptedPartners(userId, { periodId, limit: 200 });
      setAcceptedPartners(rows || []);
    } finally {
      setPartnersLoading(false);
    }
  };

  const statuses = daily?.statuses || [];
  const chartData = useMemo(() => {
    if (!daily?.data?.length) return null;
    const labels = daily.data.map(r => dayjs(r.date).format('MMM D'));
    const palette = ['#702ad0','#10b981','#ef4444','#f59e0b','#3b82f6','#a78bfa','#22c55e','#f97316','#06b6d4','#e11d48'];
    const datasets = statuses.map((s, idx) => ({
      label: s,
      data: daily.data.map(r => r[s] ?? 0),
      backgroundColor: palette[idx % palette.length],
      stack: 's'
    }));
    return { labels, datasets };
  }, [daily, statuses]);

  return (
    <Box>
      <PageHeader title="Connections" subtitle="Fans connecting with creators" />

      {/* Right now. Independent of the period picker below: it is the standing
          state, read by the same code that sends the admin alerts. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
          <Typography variant="h6">Right now</Typography>
          {live?.asOf && (
            <Typography variant="caption" color="text.secondary">
              as of {dayjs(live.asOf).format('h:mm A')} · updates every minute
            </Typography>
          )}
        </Box>

        {!liveConnections && !liveError && (
          <Box sx={{ display:'flex', justifyContent:'center', p:2 }}><CircularProgress size={24} /></Box>
        )}
        {(liveConnections ? liveConnections.ok === false : Boolean(liveError)) && (
          <Alert severity="error">
            Live connection counts are unavailable: {liveConnections?.error || liveError}. This is a failed read, not zero connections.
          </Alert>
        )}

        {liveConnections?.ok && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Active now" value={liveConnections.active} color="success" helpText={`Accepted and in conversation${bookedCallsNote(liveConnections.bookedCalls)}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Awaiting creator" value={liveConnections.awaitingCreator} color={liveConnections.awaitingCreator > 0 ? 'warning' : 'primary'} helpText="Requested, not yet answered" /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title={`Requested (${liveConnections.windowDays} days)`} value={liveConnections.requestedRecent} helpText={`${liveConnections.requested24h} in the last 24 hours`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title={`Accepted (${liveConnections.windowDays} days)`} value={liveConnections.acceptedRecent} helpText={`${liveConnections.accepted24h} in the last 24 hours`} /></Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Open connections, and anything from the last {liveConnections.windowDays} days
            </Typography>
            {liveRows.length ? (
              <Table size="small" aria-label="open and recent connections">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Creator</TableCell>
                    <TableCell>Fan</TableCell>
                    <TableCell>How</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Accepted</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveRows.map((c) => {
                    const chip = (c.presetType === 'video' && c.status === 'accepted' ? BOOKED_CHIP : STATUS_CHIP[c.status])
                      || { label: c.status || 'unknown', color: 'default' };
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Chip size="small" color={chip.color} label={chip.label} variant={chip.color === 'default' ? 'outlined' : 'filled'} />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/user/${c.creatorId}`}
                            state={{ from: { label: 'Connections', path: '/connections' } }}
                            style={{ textDecoration:'none', color: theme.palette.primary.main, fontWeight:600 }}
                          >
                            {c.creatorUsername || `User #${c.creatorId}`}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {c.fanId ? (
                            <Link
                              to={`/user/${c.fanId}`}
                              state={{ from: { label: 'Connections', path: '/connections' } }}
                              style={{ textDecoration:'none', color: theme.palette.primary.main }}
                            >
                              {c.fanUsername || `User #${c.fanId}`}
                            </Link>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {(c.presetType === 'video' ? 'Video call' : CONNECTION_TYPE[c.type]) || '—'}
                          {c.presetLabel ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>{c.presetLabel}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{when(c.requestedAt)}</TableCell>
                        <TableCell>{when(c.acceptedAt)}</TableCell>
                        <TableCell align="right">
                          {c.acceptedAt && c.fanId && c.presetType !== 'video' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewConversation(
                                { userId: c.creatorId, username: c.creatorUsername },
                                { partnerId: c.fanId, username: c.fanUsername }
                              )}
                            >
                              View conversation
                            </Button>
                          ) : null}
                          {c.presetType === 'video' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              component={Link}
                              to={`/video-bookings/${c.id}`}
                              state={{ from: { label: 'Connections', path: '/connections' } }}
                            >
                              Open booking
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No open connections, and none requested or accepted in the last {liveConnections.windowDays} days.
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* History: everything below follows the period picker. */}
      <Paper sx={{ p: 2, mb: 2, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 2, flexWrap:'wrap' }}>
        <Box>
          <Typography variant="h6">History</Typography>
          <Typography variant="caption" color="text.secondary">
            Counted by the date of each connection’s latest status change
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="period-select-label">Bidding period</InputLabel>
          <Select labelId="period-select-label" value={periodId} label="Bidding period" onChange={(e)=>setPeriodId(e.target.value)}>
            <MenuItem value="all"><em>All Time</em></MenuItem>
            {periods.map(p => (
              <MenuItem key={p.id} value={p.id}>ID {p.id} - ({dayjs(p.start_time).format('MM/DD/YY')})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {loading && !summary ? (
        <Box sx={{ display:'flex', justifyContent:'center', p:4 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPI row */}
          <Grid container spacing={3} sx={{ mb: 2 }}>
            {/* "Mutual" is gone: it counted two daters bidding on each other,
                which has no creator-fan meaning. */}
            <Grid item xs={12} sm={6} md={4}><StatCard title="Connection requests" value={summary?.total || 0} /></Grid>
            <Grid item xs={12} sm={6} md={4}><StatCard title="Accepted" value={summary?.accepted || 0} /></Grid>
            <Grid item xs={12} sm={6} md={4}><StatCard title="Creator rewarded" value={summary?.rewarded || 0} helpText="Completed: the creator earned the tokens" /></Grid>
          </Grid>

          {/* By-status chips display (counts) */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Counts by status</Typography>
            {summary?.byStatus?.length ? (
              <Box sx={{ display:'flex', gap: 1, flexWrap:'wrap' }}>
                {summary.byStatus.map(s => (
                  <Paper key={s.status} sx={{ p: .8, px: 1.2 }}>
                    <Typography variant="body2"><strong>{s.status}</strong>: {s.cnt}</Typography>
                  </Paper>
                ))}
              </Box>
            ) : <Typography variant="body2" color="text.secondary">No data</Typography>}
          </Paper>

          {/* Daily stacked bar */}
          <Paper sx={{ p:2, mb:2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Daily connections by status
              {summary?.start && summary?.end ? (
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  Window: {summary.start.slice(0,10)} → {summary.end.slice(0,10)}
                </Typography>
              ) : null}
            </Typography>
            {chartData ? (
              <Box sx={{ height: 360 }}>
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' } },
                    scales: { x: { stacked:true }, y: { stacked:true, beginAtZero:true, ticks:{ precision:0 } } }
                  }}
                />
              </Box>
            ) : <Typography variant="body2" color="text.secondary">No daily data</Typography>}
          </Paper>

          {/* Top accepted recipients */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p:2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Creators with the most accepted connections</Typography>
                {topAccepted?.length ? (
                  <Table size="small">
                    <TableHead><TableRow><TableCell>#</TableCell><TableCell>Creator</TableCell><TableCell align="right">Accepted</TableCell><TableCell>Last</TableCell></TableRow></TableHead>
                    <TableBody>
                      {topAccepted.map((r, i) => (
                        <React.Fragment key={r.userId}>
                          <TableRow hover>
                            <TableCell>{i+1}</TableCell>
                            <TableCell>
                              <Link
                                to={`/user/${r.userId}`}
                                state={{ from: { label: 'Connections', path: '/connections' } }}
                                style={{ textDecoration:'none', color: theme.palette.primary.main, fontWeight:600 }}
                              >
                                {r.username || `User #${r.userId}`}
                              </Link>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={() => handleAcceptedClick(r.userId)}
                              style={{ cursor:'pointer', fontWeight: expandedUserId === r.userId ? 700 : 400 }}
                              title="Click to see their fans"
                            >
                              {r.acceptedCount}
                            </TableCell>
                            <TableCell>{r.lastAcceptedAt ? dayjs(r.lastAcceptedAt).format('MMM D, YYYY') : '—'}</TableCell>
                          </TableRow>

                          {expandedUserId === r.userId && (
                            <TableRow>
                              <TableCell colSpan={4} style={{ background:'#fafafa', padding: 0 }}>
                                <Box sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Fans connected with {r.username || `User #${r.userId}`}
                                  </Typography>
                                  {partnersLoading ? (
                                    <Typography variant="body2" color="text.secondary">Loading…</Typography>
                                  ) : acceptedPartners.length ? (
                                    <Table size="small" aria-label="accepted partners">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>#</TableCell>
                                          <TableCell>Fan</TableCell>
                                          <TableCell align="right">Accepted</TableCell>
                                          <TableCell>Last</TableCell>
                                          <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {acceptedPartners.map((p, idx) => (
                                          <TableRow key={`${p.partnerId}-${idx}`}>
                                            <TableCell>{idx+1}</TableCell>
                                            <TableCell>
                                              <Link
                                                to={`/user/${p.partnerId}`}
                                                state={{ from: { label: 'Connections', path: '/connections' } }}
                                                style={{ textDecoration:'none', color: theme.palette.primary.main }}
                                              >
                                                {p.username || `User #${p.partnerId}`}
                                              </Link>
                                            </TableCell>
                                            <TableCell align="right">{p.acceptedCount}</TableCell>
                                            <TableCell>{p.lastAcceptedAt ? dayjs(p.lastAcceptedAt).format('MMM D, YYYY') : '—'}</TableCell>
                                            <TableCell align="right">
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleViewConversation(
                                                  { userId: expandedUserId, username: (topAccepted.find(t => t.userId === expandedUserId)?.username) },
                                                  p
                                                )}
                                              >
                                                View conversation
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">No fans found in this window.</Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                ) : <Typography variant="body2" color="text.secondary">No accepted connections</Typography>}
              </Paper>
            </Grid>

            {/* Rewarded recipients */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p:2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Creators rewarded for completed connections</Typography>
                {rewarded?.length ? (
                  <Table size="small">
                    <TableHead><TableRow><TableCell>#</TableCell><TableCell>Creator</TableCell><TableCell align="right">Rewarded</TableCell><TableCell>Last</TableCell></TableRow></TableHead>
                    <TableBody>
                      {rewarded.map((r, i) => (
                        <TableRow key={r.userId}>
                          <TableCell>{i+1}</TableCell>
                          <TableCell>
                            <Link
                              to={`/user/${r.userId}`}
                              state={{ from: { label: 'Connections', path: '/connections' } }}
                              style={{ textDecoration:'none', color: theme.palette.primary.main, fontWeight:600 }}
                            >
                              {r.username || `User #${r.userId}`}
                            </Link>
                          </TableCell>
                          <TableCell align="right">{r.rewardedCount}</TableCell>
                          <TableCell>{r.lastRewardedAt ? dayjs(r.lastRewardedAt).format('MMM D, YYYY') : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <Typography variant="body2" color="text.secondary">No rewarded connections</Typography>}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      {/* Chat overlay */}
      {chatOpen && chatPair && (
        <ConversationModal
          open={chatOpen}
          onClose={() => { setChatOpen(false); setChatPair(null); }}
          userId={chatPair.userId}
          partnerId={chatPair.partnerId}
          userRole={chatPair.userRole}
          partnerRole={chatPair.partnerRole}
          title={`Conversation — ${chatPair.userName || `User #${chatPair.userId}`} ↔ ${chatPair.partnerName || `User #${chatPair.partnerId}`}`}
        />
      )}
    </Box>
  );
}



